import { Path, PathCodec } from "@pomle/paths";
import { useLocation } from "@pomle/react-router-paths";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { useEffect } from "react";
import { AssignToRoomView } from "render/views/AssignToRoom";
import { AssignToSkinScanner } from "render/views/AssignToSkinScanner";
import { CheckInCompleteView } from "render/views/CheckInCompleteView";
import { ChecklistView } from "render/views/ChecklistView";
import { CheckOutView } from "render/views/CheckOutView";
import { FollowUpDermaView } from "render/views/FollowUpDermaView";
import { ImmediateRiskAssessmentView } from "render/views/ImmediateRiskAssessmentView";
import { JoinSessionView } from "render/views/JoinSessionView";
import { LocationSelectorView } from "render/views/LocationSelector";
import { NekoLensAuthView } from "render/views/NekoLensAuthView/NekoLensAuthView";
import { PreferredNameView } from "render/views/PreferredNameView";
import { SelectActiveVisit } from "render/views/SelectActiveVisit";
import { SelectVisitView } from "render/views/SelectVisit";
import { SelectVisitConfirmView } from "render/views/SelectVisitConfirm";
import { SessionJoinCompleteView } from "render/views/SessionJoinCompleteView";
import { StudyCounterSignView } from "render/views/StudyCounterSignView";
import { paths } from "./paths";
import { AssignToNoravECGDevice } from "render/views/AssignToNoravECGDevice";

function createStep<Codec extends PathCodec>(
  id: string,
  path: Path<Codec>,
  component: (
    params: ReturnType<Path<Codec>["decode"]>,
    options: { goTo: (url: string) => void; onBack: () => void }
  ) => React.ReactElement | null
) {
  return {
    id,
    path,
    component,
  };
}

export const steps = [
  createStep(
    "fe64c95d-dcb4-4d50-8018-1631c345ef3c",
    paths.locationSelector,
    (_, { goTo }) => {
      return <LocationSelectorView goTo={goTo} />;
    }
  ),

  createStep(
    "48316e68-66f4-11ee-a533-00155db6b9f6",
    paths.root,
    (_, { goTo }) => {
      const { pathname } = useLocation();

      /**
       * If the location cookie gets changed, the component will not be automatically
       * re-rendered. But now we don't have flows, where cookie is updated in background,
       * so it's acceptable.
       *
       * Ideally we should listen for `CookieChangeEvent`, but it's only available
       * in Safari 18.4 and later, and as of Apr 2025, large amount of our iPads
       * in clinics are still running iPadOS 18.3. So we keep the behavior consistent
       * across all devices.
       *
       * - https://developer.mozilla.org/en-US/docs/Web/API/CookieChangeEvent
       * - Browser version stats: https://sharedprodcur8grafana-ggh2fkd3bdhcbhea.neu.grafana.azure.com/goto/Ve1kwM1Ng?orgId=1
       */
      const parsedLocation = getAndParseLocationCookie();

      useEffect(() => {
        if (parsedLocation) {
          return;
        }

        const isDirectMatch = paths.locationSelector.match(pathname) === 0;
        if (isDirectMatch) {
          return;
        }

        goTo(paths.locationSelector.url({}));
      }, [goTo, parsedLocation, pathname]);

      return parsedLocation ? (
        <JoinSessionView goTo={goTo} parsedLocation={parsedLocation} />
      ) : null;
    }
  ),

  createStep(
    "8b1584aa-5d01-41a0-a4a1-ea842c6b99ea",
    paths.selectVisit,
    (_, { goTo, onBack }) => {
      return <SelectVisitView goTo={goTo} onBack={onBack} />;
    }
  ),

  createStep(
    "4c5c519c-7659-4c94-ac3a-d0ec4c5668f3",
    paths.selectActiveVisit,
    (_, { goTo, onBack }) => {
      return <SelectActiveVisit goTo={goTo} onBack={onBack} />;
    }
  ),

  createStep(
    "4c4566e5-213c-46b4-883c-3f38e99440ab",
    paths.selectVisitConfirm,
    (params, { goTo, onBack }) => {
      return (
        <SelectVisitConfirmView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "6090e7f4-66f4-11ee-8c44-00155db6b9f6",
    paths.assignSlot,
    (params, { goTo, onBack }) => {
      return (
        <SessionJoinCompleteView
          goTo={goTo}
          onBack={onBack}
          sessionId={params.sessionId}
          siteId={params.siteId}
        />
      );
    }
  ),

  createStep(
    "6654e21c-66f4-11ee-98e4-00155db6b9f6",
    paths.awaitingConsent,
    (params, { goTo, onBack }) => {
      return (
        <ChecklistView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "738274d6-66f4-11ee-9199-00155db6b9f6",
    paths.preferredName,
    (params, { goTo, onBack }) => {
      return (
        <PreferredNameView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "36a11b4e-97g4-41ee-3030-00118db3b9f`",
    paths.immediateRiskAssessment,
    (params, { goTo, onBack }) => {
      return (
        <ImmediateRiskAssessmentView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "6ca73322-66f4-11ee-b079-00155db6b9f6",
    paths.counterSign,
    (params, { goTo, onBack }) => {
      return (
        <StudyCounterSignView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "9214fbb7-cf2d-4fa4-a5c3-805902d036da",
    paths.assignToRoom,
    (params, { goTo }) => {
      return (
        <AssignToRoomView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "0ca071f9-aaeb-4c39-b29e-badfb416d010",
    paths.assignToSkinScanner,
    (params, { goTo }) => {
      return (
        <AssignToSkinScanner
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "96a46898-66f4-11ee-8bdd-00155db6b9f6",
    paths.checkInSuccess,
    (params, { goTo }) => {
      return (
        <CheckInCompleteView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "caa243ac-3293-4997-b036-05d8e761e99c",
    paths.assignToNoravECGDevice,
    (params, { goTo }) => {
      return (
        <AssignToNoravECGDevice
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "d1e2f3a4-5b6c-7d8e-9f0a-1b2c3d4e5f6a",
    paths.followUpDerma,
    (params, { goTo }) => {
      return (
        <FollowUpDermaView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "acd86bd7-4de3-42e0-bbe1-04a18d8a2832",
    paths.nekoLensAuth,
    (params, { onBack }) => {
      return (
        <NekoLensAuthView
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "a3290b96-66f4-11ee-ab4b-00155db6b9f6",
    paths.checkOut,
    (params, { goTo }) => {
      return (
        <CheckOutView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),
];

export function findStep(pathname: string) {
  return steps.find((step) => {
    // Look for exact match
    return step.path.match(pathname) === 0;
  });
}
